import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { defaultKeymap, history, redo, undo } from '@codemirror/commands';
import { syntaxHighlighting } from '@codemirror/language';
import {
  Annotation,
  Compartment,
  EditorState,
  StateEffect,
  StateField,
  type Transaction,
} from '@codemirror/state';
import { drawSelection, EditorView, keymap } from '@codemirror/view';

import { filterFieldHighlighter } from './decorations/highlight.js';
import type { FilterFieldProps } from './FilterField.js';
import { filterFieldLanguageExtension } from './parser/filterfield-language.js';
import type { FilterFieldError } from './types/filter-field-error.js';

export const placeholderCompartment = new Compartment();
export const suggestionsCompartment = new Compartment();
export const ariaAttributesCompartment = new Compartment();
export const disabledPropCompartment = new Compartment();
export const readOnlyPropCompartment = new Compartment();

export const stringAnnotation = Annotation.define<string>();

/**
 * Transaction annotation dispatched when deleting a statement using the widget.
 * @internal
 */
export const ANNOTATION_DELETE_STATEMENT = 'input.type.delete-statement';

/**
 * Transaction annotation dispatched when applying a suggestion.
 * @internal
 */
export const ANNOTATION_SUGGESTION_APPLY = 'suggestion.apply';

/**
 * Transaction annotation dispatched to work around missing compositionend events.
 * @internal
 */
export const ANNOTATION_COMPOSE_END = 'input.type.compose.end';

// We need to store the syntaxMode in a StateField to be able
// to access it in the generateErrorsOrDiagnostics() function
/**
 * @internal
 */
export const syntaxModeEffect =
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  StateEffect.define<FilterFieldProps['syntax']>();

/**
 * @internal
 */
// eslint-disable-next-line @typescript-eslint/no-deprecated
export const usedSyntaxMode = StateField.define<FilterFieldProps['syntax']>({
  create: () => 'complex',
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  update(value: FilterFieldProps['syntax'], transaction: Transaction) {
    for (const effect of transaction.effects) {
      if (effect.is(syntaxModeEffect)) {
        value = effect.value;
      }
    }

    return value;
  },
});

/**
 * We need to store the validatorMap in a StateField to be able
 * to access it in the generateErrorsOrDiagnostics() function
 * @internal
 */
export const validatorMapEffect =
  StateEffect.define<FilterFieldProps['validatorMap']>();

/**
 * @internal
 */
export const usedValidatorMap = StateField.define<
  FilterFieldProps['validatorMap']
>({
  create: () => undefined,
  update(value: FilterFieldProps['validatorMap'], transaction: Transaction) {
    for (const effect of transaction.effects) {
      if (effect.is(validatorMapEffect)) {
        value = effect.value;
      }
    }

    return value;
  },
});

/**
 * State field to store the filter field errors.
 * They are generated in the tree conversion and then re-used in the plugins for
 * creating the error decorations and tooltips.
 * @internal
 */
export const errorsEffect = StateEffect.define<{
  errors: FilterFieldError[];
}>();

/**
 * @internal
 */
export const usedErrors = StateField.define<{ errors: FilterFieldError[] }>({
  create() {
    return { errors: [] };
  },
  update(value: { errors: FilterFieldError[] }, transaction: Transaction) {
    for (const effect of transaction.effects) {
      if (effect.is(errorsEffect)) {
        value.errors = [];
      }
    }

    return value;
  },
});

/**
 * List of keys that are handled separately and should not trigger the default behavior.
 * @internal
 */
export const overrideKeys = ['ArrowUp', 'ArrowDown', 'Enter', 'Mod-Enter'];

/**
 * List of static editor extensions.
 * @internal
 */
export const staticEditorExtensions = [
  /**
   * General
   */
  // Enable line wrapping so that overflowing text is wrapped into the next line.
  EditorView.lineWrapping,
  // Enable multiple selections (CMD / ctrl + drag).
  EditorState.allowMultipleSelections.of(true),
  // Provides a undo/redo history
  history(),
  // Draw custom cursor.
  drawSelection(),

  keymap.of([
    ...defaultKeymap.filter(({ key }) => !overrideKeys.includes(key ?? '')),
    ...closeBracketsKeymap,
    {
      key: 'Mod-z',
      run: undo,
    },
    {
      key: 'Mod-y',
      run: redo,
    },
    {
      key: 'Mod-Shift-z',
      run: redo,
    },
  ]),

  /**
   * Filter field language + syntax highlighting.
   *
   * Error highlighting is also done with the highlight extensions, since using an actual
   * linter, potential errors only show up after a short (configurable) delay. As soon as
   * you type something again, the errors disappear and then show up again if the input is
   * still invalid. Using the decoration plugins, we can immediately show errors.
   * The actual linter extension is only used to show tooltips with proper error messages.
   */
  // The extension for parsing the filter field language into a syntax tree.
  filterFieldLanguageExtension,
  // Automatically close brackets.
  closeBrackets(),
  usedSyntaxMode,
  usedValidatorMap,
  usedErrors,

  // Enable highlighting using the defined highlighter.
  // This adds the spans and classes used to style the different parts of a filter.
  syntaxHighlighting(filterFieldHighlighter),
];
